import sanitizeHtml from 'sanitize-html';

// If there is no meaningful text then remove any remnants of formatting.
// One (intentional) implication of this is that clicking out of an empty
// shape and then editing it again will reset the alignment to centered.
function stripEmptyHTML(html: string): string | null {
  const element = document.createElement('div');
  element.innerHTML = html;
  const s = element.innerText.trim();
  if (s.length === 0) {
    return null;
  } else {
    return html;
  }
}

// Limit HTML to only the tags that we support in shapes.
function regularSanitizer(html: string) {
  return sanitizeHtml(html, {
    allowedTags: [
      'p',
      'u',
      'i',
      'em',
      'strong',
      'b',
      'del',
      'a',
      'li',
      'ul',
      'ol',
      'pre',
      'code',
      'br',
      'span',
      'blockquote',
      'hr',
      'h1',
      'h2',
      'h3',
      'h4',
    ],
    selfClosing: ['br', 'hr'],
    allowedAttributes: {
      '*': ['style', 'class'],
      a: ['href'],
      code: ['data-language'],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedStyles: {
      '*': {
        // Match HEX, rgb, rgba
        color: [
          /^#(0x)?[0-9a-f]+$/i,
          /^rgb\(\d+,\s*\d+,\s*\d+\)$/,
          /^rgba\(\d+,\s*\d+,\s*\d+,\s*(0|1|0?\.\d+)\)$/,
        ],
        'text-align': [/^left$/, /^right$/, /^center$/],
        // Match any number with px, em, or %
        'font-size': [/^\d+px$/],
      },
    },
  });
}

// Limit HTML to only the tags that we support in frames.
function simpleSanitizer(html: string) {
  return sanitizeHtml(html, {
    allowedTags: ['p', 'br'],
    selfClosing: ['br'],
  });
}

function getPaddingCssFormat({ top, right, bottom, left }) {
  // add a last 'px' for the final number
  return `${[top, right, bottom, left].join('px ')}px`;
}

const div = document.createElement('div');

export function escapeTextContent(text: string): string {
  div.innerText = text;
  return div.innerHTML;
}

// Return plain text for the HTML.
export function plainText(html: string) {
  const parser = new DOMParser();
  // insert a space before newline tags so that e.g. `<p>a</p><p>b<br />c</p>` becomes `a b c` not `abc`
  const doc = parser.parseFromString(
    html.replace(/<\/(p|div)>/g, ' </$1>').replaceAll('<br />', ' <br />'),
    'text/html'
  );

  return doc.body.textContent?.trim() ?? '';
}

export {
  stripEmptyHTML,
  regularSanitizer,
  simpleSanitizer,
  getPaddingCssFormat,
};
